import React, { useState, useEffect } from 'react';
import { X, Upload, Image as ImageIcon, Save } from 'lucide-react';
import Modal from '../ui/Modal';
import Input from '../ui/Input';
import Button from '../ui/Button';
import AuthImage from '../common/AuthImage';

const PreguntaModal = ({ isOpen, onClose, onSave, initialData, examenId }) => {
  const [formData, setFormData] = useState({
    texto: '',
    tipo: 'verdadero_falso',
    peso: 10
  });
  const [imagenFile, setImagenFile] = useState(null);
  const [previewImagen, setPreviewImagen] = useState(null);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (initialData) {
      setFormData({
        texto: initialData.texto || '',
        tipo: initialData.tipo || 'verdadero_falso',
        peso: initialData.peso || 10
      });
      // Mostrar imagen existente si hay
      if (initialData.imagen || initialData.urlImagen) {
        setPreviewImagen(initialData.imagen || initialData.urlImagen);
      } else {
        setPreviewImagen(null);
      }
      setImagenFile(null); // No hay archivo nuevo al inicio
    } else {
      setFormData({
        texto: '',
        tipo: 'verdadero_falso',
        peso: 10
      });
      setImagenFile(null);
      setPreviewImagen(null);
    }
    setErrors({});
  }, [initialData, isOpen]);

  const handleImagenChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validar tamaño (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setErrors({ ...errors, imagen: 'La imagen no debe superar 5MB' });
        return;
      }
      
      // Validar tipo
      if (!file.type.startsWith('image/')) {
        setErrors({ ...errors, imagen: 'Solo se permiten imágenes' });
        return;
      }

      setImagenFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewImagen(reader.result);
      };
      reader.readAsDataURL(file);
      setErrors({ ...errors, imagen: null });
    }
  };

  const handleRemoveImage = () => {
    setImagenFile(null);
    setPreviewImagen(null);
  };

  const validate = () => {
    const newErrors = {};
    
    if (!formData.texto.trim()) {
      newErrors.texto = 'El texto de la pregunta es obligatorio';
    }
    
    if (!formData.peso || formData.peso <= 0) {
      newErrors.peso = 'El peso debe ser mayor a 0';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!validate()) {
      return;
    }

    // Si hay una imagen nueva, usar FormData
    if (imagenFile) {
      const formDataToSend = new FormData();
      
      const preguntaJson = {
        texto: formData.texto,
        tipo: formData.tipo,
        peso: parseFloat(formData.peso),
        examenId: parseInt(examenId),
        opciones: []
      };
      
      const jsonBlob = new Blob([JSON.stringify(preguntaJson)], { type: 'application/json' });
      formDataToSend.append('pregunta', jsonBlob);
      formDataToSend.append('imagen', imagenFile);

      onSave(formDataToSend, initialData?.id, true); // true = tiene imagen
    } else {
      // Si NO hay imagen nueva, enviar solo JSON
      const preguntaData = {
        texto: formData.texto,
        tipo: formData.tipo,
        peso: parseFloat(formData.peso),
        examenId: parseInt(examenId),
        opciones: []
      };

      onSave(preguntaData, initialData?.id, false); // false = sin imagen nueva
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={initialData ? '✏️ Editar Pregunta' : 'Nueva Pregunta'}
      size="lg"
    >
      <form onSubmit={handleSubmit} className="space-y-5">
        {/* Texto de la pregunta */}
        <div>
          <label className="block text-sm font-semibold text-neutral-700 mb-2">
            Texto de la Pregunta <span className="text-red-500">*</span>
          </label>
          <textarea
            value={formData.texto}
            onChange={(e) => setFormData({ ...formData, texto: e.target.value })}
            placeholder="Escribe aquí la pregunta que deseas hacer..."
            className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent transition-all resize-none ${
              errors.texto ? 'border-red-500' : 'border-neutral-300'
            }`}
            rows={4}
          />
          {errors.texto && (
            <p className="text-red-500 text-sm mt-1">{errors.texto}</p>
          )}
        </div>

        {/* Imagen */}
        <div>
          <label className="block text-sm font-semibold text-neutral-700 mb-2">
            <ImageIcon className="w-4 h-4 inline mr-2" />
            Imagen (Opcional)
          </label>
          
          {!previewImagen ? (
            <label className="cursor-pointer">
              <div className="flex flex-col items-center justify-center gap-3 px-6 py-8 border-2 border-dashed border-neutral-300 rounded-xl hover:border-primary hover:bg-primary/5 transition-all group">
                <div className="w-16 h-16 rounded-full bg-neutral-100 group-hover:bg-primary/10 flex items-center justify-center transition-colors">
                  <Upload className="w-8 h-8 text-neutral-400 group-hover:text-primary transition-colors" />
                </div>
                <div className="text-center">
                  <p className="text-sm font-medium text-neutral-700 group-hover:text-primary transition-colors">
                    Click para seleccionar una imagen
                  </p>
                  <p className="text-xs text-neutral-500 mt-1">
                    PNG, JPG, GIF hasta 5MB
                  </p>
                </div>
              </div>
              <input
                type="file"
                accept="image/*"
                onChange={handleImagenChange}
                className="hidden"
              />
            </label>
          ) : (
            <div className="relative">
              {/* Si es una imagen nueva (File), mostrar con img normal */}
              {imagenFile ? (
                <img 
                  src={previewImagen} 
                  alt="Preview" 
                  className="w-full h-64 object-contain rounded-xl border-2 border-neutral-200 bg-neutral-50"
                />
              ) : (
                /* Si es una imagen existente (URL), usar AuthImage */
                <AuthImage
                  src={previewImagen}
                  alt="Imagen actual"
                  className="w-full h-64 object-contain rounded-xl border-2 border-neutral-200 bg-neutral-50"
                />
              )}
              <button
                type="button"
                onClick={handleRemoveImage}
                className="absolute top-3 right-3 p-2 bg-red-500 hover:bg-red-600 text-white rounded-full shadow-lg transition-colors"
                title="Eliminar imagen"
              >
                <X className="w-4 h-4" />
              </button>
              {imagenFile && (
                <div className="absolute bottom-3 left-3 bg-black/70 backdrop-blur-sm text-white text-xs px-3 py-1.5 rounded-full">
                  {imagenFile?.name}
                </div>
              )}
            </div>
          )}
          
          {errors.imagen && (
            <p className="text-red-500 text-sm mt-1">{errors.imagen}</p>
          )}
        </div>

        {/* Tipo y Peso */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-semibold text-neutral-700 mb-2">
              Tipo de Pregunta <span className="text-red-500">*</span>
            </label>
            <select
              value={formData.tipo}
              onChange={(e) => setFormData({ ...formData, tipo: e.target.value })}
              className="w-full px-4 py-3 border border-neutral-300 rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
            >
              <option value="verdadero_falso">Verdadero/Falso</option>
              <option value="multiple_choice">Opción Múltiple</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-semibold text-neutral-700 mb-2">
              Peso (Puntos) <span className="text-red-500">*</span>
            </label>
            <Input
              type="number"
              value={formData.peso}
              onChange={(e) => setFormData({ ...formData, peso: e.target.value })}
              placeholder="10"
              min="0"
              step="0.5"
              className={errors.peso ? 'border-red-500' : ''}
            />
            {errors.peso && (
              <p className="text-red-500 text-sm mt-1">{errors.peso}</p>
            )}
          </div>
        </div>

        {/* Información adicional */}
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
          <p className="text-sm text-blue-800">
            <strong>💡 Consejo:</strong> Después de crear la pregunta, podrás agregar las opciones de respuesta.
          </p>
        </div>

        {/* Botones */}
        <div className="flex justify-end gap-3 pt-4 border-t border-neutral-200">
          <Button 
            type="button" 
            variant="outline" 
            onClick={onClose}
            className="px-6"
          >
            Cancelar
          </Button>
          <Button 
            type="submit" 
            variant="primary"
            className="px-6 gap-2"
          >
            <Save className="w-4 h-4" />
            {initialData ? 'Actualizar' : 'Crear'} Pregunta
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default PreguntaModal;
